import {FC, useEffect, useState} from 'react'
import {useQueryClient} from 'react-query'
import {useLocation} from 'react-router-dom'
import {PaymentResult} from '../core/onlinePaymentApi'
import {
  clearPaymentParam,
  PaymentResultBanner,
  readPaymentParam,
} from '../components/PaymentResultBanner'
import {
  clearWalletResume,
  markWalletResumeReady,
  readWalletResume,
  WalletResume,
} from './walletResume'

/** How long a page has to take up the remembered step before the banner asks the user to repeat it. */
const TAKE_UP_MS = 2500

const isWalletPage = (pathname: string) => /\/setting\/\d+\/wallet\/?$/.test(pathname)

/**
 * After «افزایش موجودی و ادامه» the gateway brings the payer back to the page of the step (?payment=…): the payment's
 * result is shown on top of that page, and once the credit is in the wallet the remembered step goes on there by
 * itself (useWalletResume). A step whose page cannot take it up (a file that has to be chosen again) is named, so the
 * user repeats it. The wallet page shows its own result; an invoice payment never comes here.
 */
export const WalletReturnHost: FC = () => {
  const location = useLocation()
  const queryClient = useQueryClient()
  const [publicId, setPublicId] = useState<string>()
  const [resume, setResume] = useState<WalletResume>()
  const [outcome, setOutcome] = useState<'resumed' | 'repeat'>()

  useEffect(() => {
    const payment = readPaymentParam()
    const remembered = readWalletResume()
    // Only a charge this tab started for a step on this very page.
    if (
      !payment ||
      !remembered ||
      isWalletPage(location.pathname) ||
      !remembered.returnPath.startsWith(location.pathname)
    )
      return
    setPublicId(payment)
    setResume(remembered)
    setOutcome(undefined)
  }, [location.pathname, location.search])

  if (!publicId || !resume) return null

  const settled = (result: PaymentResult) => {
    queryClient.invalidateQueries(['wallet'])
    queryClient.invalidateQueries(['business-services'])
    const credited =
      result.purpose === 'wallet' &&
      result.status === 'paid' &&
      (result.apply === 'applied' || result.apply === 'notApplicable')
    if (!credited) {
      // Not paid (or not credited yet): the step stays where it stopped; the user may charge again.
      clearWalletResume()
      return
    }

    markWalletResumeReady(publicId)
    window.setTimeout(() => {
      const left = readWalletResume()
      if (left?.readyFor) {
        // No page took it up: forget it, so it never runs by surprise later.
        clearWalletResume()
        setOutcome('repeat')
      } else {
        setOutcome('resumed')
      }
    }, TAKE_UP_MS)
  }

  const close = () => {
    clearPaymentParam()
    setPublicId(undefined)
    setResume(undefined)
  }

  return (
    <div className='app-container container-fluid pt-4'>
      <PaymentResultBanner publicId={publicId} onSettled={settled} onClose={close} />
      {outcome === 'resumed' && (
        <div className='alert alert-light-success py-2 mt-2 mb-0' role='status'>
          «{resume.label}» پس از افزایش موجودی ادامه پیدا کرد.
        </div>
      )}
      {outcome === 'repeat' && (
        <div className='alert alert-light-primary py-2 mt-2 mb-0' role='status'>
          موجودی کیف پول افزایش یافت؛ اکنون «{resume.label}» را دوباره انجام دهید.
        </div>
      )}
    </div>
  )
}
